import React, { useCallback, useContext, useState } from 'react';
import { IconButton, Link, Stack, Text } from '@fluentui/react';
import { ThemeContext } from '../../HubLayout/models/ThemeContext';
import { getThemeFromString } from '../util/localStorageUtils';

export interface ICopyableValueProps {
    label: string;
    value: string;
    /** When set, the value renders as an external link as well as being copyable. */
    href?: string;
    /** Identifiers and endpoints read better monospaced; prose does not. */
    monospace?: boolean;
    /** Allow the value to wrap over several lines, e.g. a Kusto query. */
    multiline?: boolean;
}

/**
 * A labelled value in a bordered box with a copy button, matching `DetailsPanelTextBox` on the
 * release details pane and the cluster banner on the Kusto pages. Use for anything a reader is
 * likely to paste somewhere else: URLs, tenant ids, object ids, security groups, queries.
 */
export const CopyableValue: React.FC<ICopyableValueProps> = ({
    label,
    value,
    href,
    monospace = true,
    multiline = false,
}) => {
    const themeContext = useContext(ThemeContext);
    const theme = getThemeFromString(themeContext.themeName);

    const [copied, setCopied] = useState<boolean>(false);

    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard access can be denied; the value stays selectable on the page.
        }
    }, [value]);

    const valueStyles = {
        root: {
            fontFamily: monospace ? 'Consolas, Menlo, monospace' : undefined,
            wordBreak: 'break-all' as const,
            whiteSpace: multiline ? ('pre-wrap' as const) : undefined,
        },
    };

    return (
        <Stack tokens={{ childrenGap: 6 }}>
            <Text
                variant="smallPlus"
                styles={{
                    root: {
                        fontWeight: 600,
                        color: theme.palette.neutralSecondary,
                    },
                }}
            >
                {label}
            </Text>
            <Stack
                horizontal
                verticalAlign={multiline ? 'start' : 'center'}
                tokens={{ childrenGap: 8 }}
                styles={{
                    root: {
                        backgroundColor: theme.palette.neutralLighter,
                        border: `1px solid ${theme.palette.neutralLight}`,
                        borderRadius: 4,
                        padding: '4px 4px 4px 12px',
                        minHeight: 36,
                    },
                }}
            >
                <Stack.Item grow styles={{ root: { minWidth: 0 } }}>
                    {href ? (
                        <Link
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            styles={valueStyles}
                        >
                            {value}
                        </Link>
                    ) : (
                        <Text variant="medium" styles={valueStyles}>
                            {value}
                        </Text>
                    )}
                </Stack.Item>
                <IconButton
                    iconProps={{ iconName: copied ? 'CheckMark' : 'Copy' }}
                    title={copied ? 'Copied' : 'Copy to clipboard'}
                    ariaLabel={
                        copied
                            ? `${label} copied to clipboard`
                            : `Copy ${label} to clipboard`
                    }
                    onClick={() => void copy()}
                    styles={{ root: { flexShrink: 0 } }}
                />
            </Stack>
        </Stack>
    );
};
